import React, { useEffect, useState } from 'react';
import {
  BubbleAlert,
  BubbleButton,
  BubbleDebounce,
  BubbleFormGroup,
  BubbleInput,
  BubbleSelect,
  BubbleTextArea,
  BubbleTypeahead,
} from '../BubbleUI';
import { Alert } from '@spark-labs/avocado-ui';
import { FormGenericContainer } from '../FormGenericContainer';
import {
  createProperty,
  toggleArchive,
  updateConfigurations,
  updateProperty,
} from '@go-client/lib/api/property';
import { useServerInfo } from '@go-client/context/ServerInfoContext';
import {
  BareProperty,
  Property,
  PropertySettings,
  PropertyType,
  PropertyZone,
} from '@go-shared/types/property';
import { createPortal } from 'react-dom';
import { BareVenueTemplate, VenueOrder } from '@go-shared/types/venueTemplate';
import { getVenueTemplatesByPropertyId, updateVenueOrder } from '@go-client/lib/api/venueTemplate';
import { HourFormat } from '@go-shared/types/brand';
import { canArchiveProperty } from '@go-shared/lib/permissions';
import { useUserInfo } from '@go-client/context/UserInfoContext';
import { ObjectId } from 'bson';
import { CopyToClipboardButton } from '../CopyToClipboard';

interface PropertyFormDataBase {
  name: string;
  description: string;
  location: string;
  type: PropertyType;
  zones: PropertyZone[];
  venueOrder: Array<VenueOrder>;
  config?: PropertySettings;
}

type PropertyFormData = PropertyFormDataBase & {
  _dirty: boolean;
  _errors: Partial<PropertyFormError>;
};

type PropertyFormError = { [K in keyof PropertyFormDataBase]: string };

function autoFixVenueOrder(venueOrder: VenueOrder[], skipOrder?: boolean) {
  venueOrder.sort((a, b) => a.order - b.order || a.name.localeCompare(b.name));

  if (!skipOrder) {
    venueOrder.forEach((o, i) => {
      o.order = i + 1;
    });
  }
}

function DEFAULT_FORM_DATA(
  property?: Property | BareProperty,
  venueTemplates?: BareVenueTemplate[]
): PropertyFormData {
  const venueOrder = (venueTemplates || []).map(v => ({
    id: v._id,
    name: v.name,
    order: v.order,
    archived: v.archived,
  }));

  autoFixVenueOrder(venueOrder);

  return {
    name: property?.name ?? '',
    type: property?.type ?? 'sea',
    description: property?.description ?? '',
    location: property?.location ?? '',
    venueOrder,
    zones: property?.zones ?? [],
    config: property?.settings,
    _dirty: false,
    _errors: {},
  };
}

interface PropertyFormProps {
  edit?: BareProperty;
  appearance: 'panel' | 'basic' | 'split';
  onClose?: () => void;
  actionsElem?: HTMLDivElement;
  onFormSubmitted: () => Promise<void>;
}

const hourFormatOptions: Array<{ value: HourFormat | undefined; label: string }> = [
  {
    value: undefined,
    label: 'Use brand default',
  },
  {
    value: '12',
    label: '12',
  },
  {
    value: '24',
    label: '24',
  },
];

export default function PropertyForm(props: PropertyFormProps) {
  const serverInfo = useServerInfo();
  const { brand } = serverInfo;
  const { user } = useUserInfo();
  const [saving, setSaving] = useState(false);
  const [formData, setFormData] = useState<PropertyFormData>(DEFAULT_FORM_DATA(props.edit));
  const [hourFormatValue, setHourFormatValue] = useState(
    hourFormatOptions.filter(opt => opt.value === props?.edit?.settings?.hourFormat).pop()
  );

  useEffect(() => {
    resetForm();
  }, [props.edit]);

  async function resetForm() {
    let venueTemplates: BareVenueTemplate[] | undefined = undefined;

    if (props.edit) {
      venueTemplates = await getVenueTemplatesByPropertyId(props.edit._id);
    }

    setFormData(DEFAULT_FORM_DATA(props.edit, venueTemplates));
  }

  function changeForm<T extends keyof PropertyFormDataBase>(key: T, val: PropertyFormDataBase[T]) {
    setFormData({ ...formData, [key]: val, _dirty: true });
  }

  async function submitForm() {
    setSaving(true);
    try {
      if (props.edit) {
        // user intends to edit an existing property
        await updateProperty(props.edit._id, {
          ...props.edit,
          name: formData.name,
          description: formData.description,
          location: formData.location,
          zones: formData.zones,
        });
        await updateVenueOrder(props.edit._id, formData.venueOrder);

        if (formData.config) {
          await updateConfigurations(props.edit._id, formData.config);
        }
      } else {
        // user intents to create a new property
        await createProperty(brand._id, {
          name: formData.name,
          type: formData.type,
          description: formData.description,
          location: formData.location,
          zones: formData.zones,
          brand: brand._id,
          // FIXME: this should come from the form
          dayStart: { hour: 6, minute: 0 },
        });
      }

      await props.onFormSubmitted();
      if (props.onClose) props.onClose();
    } finally {
      setSaving(false);
    }
  }

  async function handleToggleArchive() {
    if (!props.edit) return;

    setSaving(true);

    try {
      const yes = await Alert.question(
        `Are you sure you wish to ${props.edit.active ? 'archive' : 'restore'} this property?`
      );
      if (yes) await toggleArchive(props.edit._id, !props.edit.active);
      await props.onFormSubmitted();
    } finally {
      setSaving(false);
    }
  }

  const zoneOpts = formData.zones.map(z => ({
    key: z._id,
    refKey: z._id,
    item: z,
  }));

  const errored = Object.keys(formData._errors).length > 0;

  const formHtml = (
    <div className="grid grid-cols-3 gap-4">
      {props.edit && !props.edit.active && (
        <BubbleAlert variant="banana" containerClassName="col-span-3">
          This property is archived. It will not show up on the properties page by default.
        </BubbleAlert>
      )}
      <BubbleFormGroup label="Name" error={formData._errors.name} className="col-span-3" asterisk>
        <BubbleInput
          block
          type="text"
          value={formData.name}
          onValue={val => changeForm('name', val)}
        />
      </BubbleFormGroup>
      <BubbleFormGroup
        label="Description"
        error={formData._errors.description}
        className="col-span-3"
      >
        <BubbleTextArea
          block
          value={formData.description}
          onValue={val => changeForm('description', val)}
        />
      </BubbleFormGroup>
      <BubbleFormGroup label="Location" error={formData._errors.location} className="col-span-3">
        <BubbleInput
          block
          type="text"
          value={formData.location}
          onValue={val => changeForm('location', val)}
        />
      </BubbleFormGroup>
      <BubbleFormGroup label="Hour Format" className="col-span-3">
        <div className="flex items-start">
          <BubbleSelect
            block
            allowUndefined={false}
            value={hourFormatValue}
            options={hourFormatOptions}
            labelExtractor={opt => opt.label}
            onValue={selected => {
              setHourFormatValue(selected);
              changeForm('config', { ...formData.config, hourFormat: selected?.value });
            }}
          />
        </div>
      </BubbleFormGroup>
      {user.superAdmin && (
        <BubbleFormGroup label="Audiences" className="col-span-3">
          <BubbleTypeahead
            block
            options={zoneOpts}
            placeholder="Enter audience name..."
            menuClassName="max-h-80 overflow-y-auto"
            allowNew
            noSuggestionsClick
            alwaysShowSuggestions={zoneOpts.length > 0}
            suggestionsText="No similar audiences found"
            suggestionClassName="p-1-i"
            suggestionsRenderFn={(_, opt) => (
              <div className="flex items-center">
                <BubbleInput
                  type="text"
                  block
                  appearance="bare"
                  value={opt.item.name}
                  className="flex-grow"
                  inputClassName="px-3 py-1 rounded"
                  onValue={val => {
                    const zIdx = formData.zones.findIndex(z => z._id === opt.item._id);
                    if (zIdx < 0) return;
                    const newZones = [...formData.zones];
                    newZones.splice(zIdx, 1, { ...opt.item, name: val });
                    changeForm('zones', newZones);
                  }}
                />
                <span className="text-xs font-semibold uppercase text-navy mr-3">Color</span>
                <BubbleInput
                  type="color"
                  value={opt.item.color}
                  className="mr-2"
                  onValue={val => {
                    const zIdx = formData.zones.findIndex(z => z._id === opt.item._id);
                    if (zIdx < 0) return;
                    const newZones = [...formData.zones];
                    newZones.splice(zIdx, 1, { ...opt.item, color: val });
                    changeForm('zones', newZones);
                  }}
                />
                <CopyToClipboardButton copyText={opt.item._id} label="Copy ID" />
              </div>
            )}
            onCreate={val =>
              changeForm('zones', [
                ...formData.zones,
                { _id: new ObjectId().toHexString(), name: val, color: '#000000' },
              ])
            }
            onTrash={val =>
              changeForm(
                'zones',
                formData.zones.filter(z => z._id !== val.item._id)
              )
            }
          />
        </BubbleFormGroup>
      )}
      {formData.venueOrder.length > 0 && (
        <BubbleFormGroup
          label="Venue Order"
          error={formData._errors.venueOrder}
          className="col-span-3"
          labelClassName="m-0-i"
        >
          <div className="sticky top-0 py-3 bg-white px-3 -mx-3">
            <BubbleButton
              variant="navy"
              className="mr-2"
              onClick={() => {
                const newVenueOrder = [...formData.venueOrder];
                autoFixVenueOrder(newVenueOrder);
                changeForm('venueOrder', newVenueOrder);
              }}
              icon="arrow-down-1-9"
            >
              Auto Fix Order
            </BubbleButton>
            <BubbleButton
              variant="slate"
              onClick={() => {
                const newVenueOrder = [...formData.venueOrder];
                autoFixVenueOrder(newVenueOrder);
                newVenueOrder.forEach((v, i) => {
                  v.order = (i + 1) * 10;
                });
                changeForm('venueOrder', newVenueOrder);
              }}
              icon="arrow-down-1-9"
            >
              Auto Fix Order (Padded)
            </BubbleButton>
          </div>
          <div className="columns-2">
            {formData.venueOrder.map((vo, i) => (
              <div className="flex items-center" key={vo.id}>
                <BubbleDebounce<{ val: number; idx: number }>
                  onTrigger={[
                    ({ val, idx }) => {
                      const newVenueOrder = [...formData.venueOrder];
                      newVenueOrder.splice(idx, 1, {
                        id: vo.id,
                        name: vo.name,
                        order: val,
                      });
                      autoFixVenueOrder(newVenueOrder, true);
                      changeForm('venueOrder', newVenueOrder);
                    },
                    500,
                  ]}
                >
                  {({ debounce }) => (
                    <BubbleInput
                      type="number"
                      value={vo.order}
                      className="mr-3 flex-shrink-0"
                      inputClassName="max-w-28"
                      autoSelect
                      onValue={v => debounce({ val: v, idx: i })}
                    />
                  )}
                </BubbleDebounce>
                <div className="flex-grow">{vo.name}</div>
              </div>
            ))}
          </div>
        </BubbleFormGroup>
      )}
      {!props.edit && (
        <BubbleFormGroup label="Type" className="col-span-3" error={formData._errors.type} asterisk>
          <div className="flex items-center">
            <div className="relative mr-3">
              <BubbleInput
                type="checkbox"
                className="mr-1"
                inputClassName="stretched-link"
                checked={formData.type === 'sea'}
                onValue={chk => {
                  if (chk) {
                    changeForm('type', 'sea');
                  }
                }}
              />
              <span>Ship</span>
            </div>
            <div className="relative">
              <BubbleInput
                type="checkbox"
                className="mr-1"
                inputClassName="stretched-link"
                checked={formData.type === 'shore'}
                onValue={chk => {
                  if (chk) {
                    changeForm('type', 'shore');
                  }
                }}
              />
              <span>Shore</span>
            </div>
          </div>
        </BubbleFormGroup>
      )}
    </div>
  );

  const formActions = (
    <>
      {props.edit && canArchiveProperty(user, serverInfo) && (
        <BubbleButton variant="dirt" onClick={handleToggleArchive} icon="box-archive">
          {`${props.edit.active ? 'Archive' : 'Restore'} Property`}
        </BubbleButton>
      )}
      <div className="ml-auto" />
      <BubbleButton
        variant="navy"
        onClick={resetForm}
        inverse
        disabled={!formData._dirty || errored || saving}
        icon="rotate-left"
      >
        Reset
      </BubbleButton>
      <BubbleButton
        variant="navy"
        onClick={submitForm}
        disabled={!formData._dirty || errored || saving}
        icon="floppy-disk"
      >
        Save Changes
      </BubbleButton>
    </>
  );

  switch (props.appearance) {
    case 'split':
      const portalButtons = props.actionsElem ? (
        createPortal(formActions, props.actionsElem)
      ) : (
        <></>
      );

      return (
        <>
          {formHtml}
          {portalButtons}
        </>
      );
    case 'panel':
      return (
        <div className="template-details">
          <FormGenericContainer footer={() => formActions}>
            <div className="flex items-start mb-5">
              <div>
                <h1 className="text-lg text-slate-dark font-semibold">Add New Property</h1>
                <h2 className="text-2xl text-navy">Enter Property Details</h2>
              </div>
              <BubbleButton
                onClick={props.onClose}
                className="ml-auto"
                variant="navy"
                inverse
                icon="xmark-large"
                iconSize="xl"
              />
            </div>
            {formHtml}
          </FormGenericContainer>
        </div>
      );
    default:
      return (
        <>
          {formHtml}
          {formActions}
        </>
      );
  }
}
